import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import {
  Plus, Trash2, TrendingUp, TrendingDown, Wallet, Edit,
  ChevronLeft, ChevronRight, Search, Download, FileSpreadsheet,
  ArrowUpRight, ArrowDownRight, Users, Clock, AlertCircle,
  Eye, Filter, X, BookOpen, PieChart, UserCircle, Hourglass,
  Calendar as CalendarIcon, Tag, ArrowLeftRight, FileDown, Landmark, Banknote, CalendarRange
} from 'lucide-react';
import axios from 'axios';
import CategoryManagerModal, { getColorMeta } from '../CategoryManagerModal';
import PageHeader from '../ui-pro/PageHeader';
import StatCard from '../ui-pro/StatCard';
import ProTable from '../ui-pro/ProTable';
import { downloadFile } from '../ui-pro/download';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

const INCOME_CATEGORIES = [
  { id: 'rezervasyon', label: 'Rezervasyon', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'lig_geliri', label: 'Lig Geliri', color: 'bg-blue-100 text-blue-700' },
  { id: 'kurs_ucreti', label: 'Kurs Ücreti', color: 'bg-purple-100 text-purple-700' },
  { id: 'etkinlik', label: 'Etkinlik', color: 'bg-amber-100 text-amber-700' },
  { id: 'parkur_kursu', label: 'Parkur Kursu', color: 'bg-pink-100 text-pink-700' },
  { id: 'amator_voleybol', label: 'Amatör Voleybol', color: 'bg-indigo-100 text-indigo-700' },
  { id: 'diger', label: 'Diğer Gelir', color: 'bg-slate-100 text-slate-700' }
];

const EXPENSE_CATEGORIES = [
  { id: 'maas', label: 'Maaş', color: 'bg-red-100 text-red-700' },
  { id: 'kira', label: 'Kira', color: 'bg-orange-100 text-orange-700' },
  { id: 'faturalar', label: 'Faturalar', color: 'bg-yellow-100 text-yellow-700' },
  { id: 'malzeme', label: 'Malzeme', color: 'bg-rose-100 text-rose-700' },
  { id: 'bakim', label: 'Bakım', color: 'bg-fuchsia-100 text-fuchsia-700' },
  { id: 'lig_gideri', label: 'Lig Gideri', color: 'bg-cyan-100 text-cyan-700' },
  { id: 'parkur_kursu', label: 'Parkur Kursu', color: 'bg-pink-100 text-pink-700' },
  { id: 'amator_voleybol', label: 'Amatör Voleybol', color: 'bg-indigo-100 text-indigo-700' },
  { id: 'diger', label: 'Diğer Gider', color: 'bg-slate-100 text-slate-700' }
];

const PAYMENT_METHOD_FALLBACK = [
  { id: 'nakit', label: 'Nakit', icon: '💵', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { id: 'garanti_bbva', label: 'Garanti BBVA', icon: '🏦', color: 'bg-red-100 text-red-700 border-red-200' },
  { id: 'bostanci', label: 'Bostancı', icon: '🏢', color: 'bg-amber-100 text-amber-700 border-amber-200' }
];

import FixedExpensesTab from '../FixedExpensesTab';

const formatTL = (n) => `₺${(parseFloat(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
};

const FinancialsTab = ({
  incomes = [],
  expenses = [],
  customers = [],
  reservations = [],
  refereesTotalBalance,
  fetchIncomes,
  fetchExpenses,
  fetchAnalytics,
  fetchRefereesTotalBalance,
  initialSubTab,
  quickAction,
  onQuickActionConsumed
}) => {
  // Ay state
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState(initialSubTab || 'ledger');
  useEffect(() => { if (initialSubTab) setActiveSubTab(initialSubTab); }, [initialSubTab]);

  // Kategori yönetimi
  const [customCategories, setCustomCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const fetchCategoriesList = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCustomCategories(res.data || []);
    } catch (e) { /* yoksay */ }
  };

  useEffect(() => {
    fetchCategoriesList();
  }, []);

  // Kasa/banka hesapları artık veritabanından gelir (Kasalar ekranından yönetilir)
  const [cashAccounts, setCashAccounts] = useState([]);
  useEffect(() => {
    axios.get(`${API}/cash-accounts`)
      .then(r => setCashAccounts(r.data?.accounts || []))
      .catch(() => { /* yetki yoksa varsayılan listeye düşülür */ });
  }, []);
  const PAYMENT_METHODS = useMemo(() => {
    const active = cashAccounts.filter(a => a.is_active !== false);
    if (!active.length) return PAYMENT_METHOD_FALLBACK;
    return active.map(a => ({
      id: a.key,
      label: a.name,
      icon: a.type === 'cash' ? '💵' : '🏦',
      infoOnly: !!a.info_only,
      color: a.info_only ? 'bg-amber-100 text-amber-700 border-amber-200'
        : a.type === 'cash' ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
          : 'bg-sky-100 text-[#176B91] border-sky-200',
    }));
  }, [cashAccounts]);

  // Ay seçici değişince o ayın gelir/gider verisini backend'den yeniden çek
  // (aksi halde yalnızca güncel ay yüklü olduğundan geçmiş aylar boş görünür)
  const kasaMonthStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
  useEffect(() => {
    fetchIncomes?.(kasaMonthStr);
    fetchExpenses?.(kasaMonthStr);
  }, [kasaMonthStr]);

  // Slug oluştur (kategori name → id formatı)
  const slugify = (s) => (s || '').toLowerCase()
    .replace(/ı/g, 'i').replace(/İ/g, 'i')
    .replace(/ş/g, 's').replace(/Ş/g, 's')
    .replace(/ğ/g, 'g').replace(/Ğ/g, 'g')
    .replace(/ü/g, 'u').replace(/Ü/g, 'u')
    .replace(/ö/g, 'o').replace(/Ö/g, 'o')
    .replace(/ç/g, 'c').replace(/Ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

  // Birleştirilmiş kategori listeleri (built-in + DB'den özel olanlar)
  const mergedIncomeCategories = useMemo(() => {
    const builtIns = INCOME_CATEGORIES;
    const custom = customCategories
      .filter(c => c.type === 'income' && !c.is_default)
      .map(c => {
        const cm = getColorMeta(c.color);
        return { id: slugify(c.name), label: c.name, color: cm.cls.split(' ').slice(0, 2).join(' ') };
      });
    // Çakışma engelle (aynı id varsa custom'ı atla)
    const ids = new Set(builtIns.map(b => b.id));
    return [...builtIns, ...custom.filter(c => !ids.has(c.id))];
  }, [customCategories]);

  const mergedExpenseCategories = useMemo(() => {
    const builtIns = EXPENSE_CATEGORIES;
    const custom = customCategories
      .filter(c => c.type === 'expense' && !c.is_default)
      .map(c => {
        const cm = getColorMeta(c.color);
        return { id: slugify(c.name), label: c.name, color: cm.cls.split(' ').slice(0, 2).join(' ') };
      });
    const ids = new Set(builtIns.map(b => b.id));
    return [...builtIns, ...custom.filter(c => !ids.has(c.id))];
  }, [customCategories]);

  // Filtreler
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // all, income, expense
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Modal states
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingTx, setEditingTx] = useState(null); // {type, ...data}
  const [detailTx, setDetailTx] = useState(null);
  const [submittingTransfer, setSubmittingTransfer] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [accountFilter, setAccountFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [orgOptions, setOrgOptions] = useState([]);
  const [exporting, setExporting] = useState(null);

  // Üst bardaki hızlı işlem butonları (Gelir/Gider Ekle) ilgili modalı açar
  useEffect(() => {
    if (!quickAction) return;
    if (quickAction === 'income') setShowIncomeModal(true);
    if (quickAction === 'expense') setShowExpenseModal(true);
    onQuickActionConsumed?.();
  }, [quickAction]); // eslint-disable-line

  // Form states
  const todayISO = new Date().toISOString().split('T')[0];
  const [incomeForm, setIncomeForm] = useState({ title: '', amount: '', category: 'diger', date: todayISO, payment_method: 'nakit', notes: '' });
  const [expenseForm, setExpenseForm] = useState({ title: '', amount: '', category: 'diger', date: todayISO, payment_method: 'nakit', notes: '' });
  const [transferForm, setTransferForm] = useState({ from_method: 'nakit', to_method: 'garanti_bbva', amount: '', date: todayISO, notes: '' });

  const submitTransfer = async (e) => {
    e.preventDefault();
    if (!transferForm.amount || parseFloat(transferForm.amount) <= 0) {
      toast.error('Geçerli bir tutar girin'); return;
    }
    if (transferForm.from_method === transferForm.to_method) {
      toast.error('Kaynak ve hedef kasa farklı olmalı'); return;
    }
    setSubmittingTransfer(true);
    try {
      await axios.post(`${API}/transfers`, {
        from_method: transferForm.from_method,
        to_method: transferForm.to_method,
        amount: parseFloat(transferForm.amount),
        date: transferForm.date,
        notes: transferForm.notes || '',
      });
      toast.success('Transfer tamamlandı');
      setShowTransferModal(false);
      setTransferForm({ from_method: 'nakit', to_method: 'garanti_bbva', amount: '', date: todayISO, notes: '' });
      fetchIncomes();
      fetchExpenses();
      fetchAnalytics?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transfer başarısız');
    } finally {
      setSubmittingTransfer(false);
    }
  };

  // Kasa hesabı bazlı dağılım (bu ay)
  // ===== Helpers =====
  const getCategoryMeta = (id, type) => {
    const cats = type === 'income' ? mergedIncomeCategories : mergedExpenseCategories;
    return cats.find(c => c.id === id) || { label: id || 'Diğer', color: 'bg-slate-100 text-slate-700' };
  };

  // Aya göre filtrele
  const monthMatches = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    return d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
  };

  // Özel tarih aralığı girildiğinde ay seçimi yerine aralık uygulanır
  const customRange = Boolean(dateFrom || dateTo);
  const inRange = (d) => {
    if (!d) return false;
    const day = String(d).slice(0, 10);
    if (dateFrom && day < dateFrom) return false;
    if (dateTo && day > dateTo) return false;
    return true;
  };
  const monthlyIncomes = useMemo(
    () => incomes.filter(i => (customRange ? inRange(i.date) : monthMatches(i.date))),
    [incomes, selectedMonth, selectedYear, dateFrom, dateTo] // eslint-disable-line
  );
  const monthlyExpenses = useMemo(
    () => expenses.filter(e => (customRange ? inRange(e.date) : monthMatches(e.date))),
    [expenses, selectedMonth, selectedYear, dateFrom, dateTo] // eslint-disable-line
  );
  const periodLabel = customRange
    ? `${dateFrom ? new Date(dateFrom).toLocaleDateString('tr-TR') : '...'} – ${dateTo ? new Date(dateTo).toLocaleDateString('tr-TR') : '...'}`
    : `${MONTHS[selectedMonth]} ${selectedYear}`;

  // Birleşik işlemler (filtreler uygulanmış)
  const ledger = useMemo(() => {
    const all = [
      ...monthlyIncomes.map(i => ({ ...i, _type: 'income' })),
      ...monthlyExpenses.map(e => ({ ...e, _type: 'expense' }))
    ];
    let filtered = all;
    if (typeFilter !== 'all') filtered = filtered.filter(t => t._type === typeFilter);
    if (categoryFilter !== 'all') filtered = filtered.filter(t => t.category === categoryFilter);
    if (accountFilter !== 'all') filtered = filtered.filter(t => (t.payment_method || 'nakit') === accountFilter);
    if (orgFilter !== 'all') {
      filtered = orgFilter === 'none'
        ? filtered.filter(t => !t.organization_id)
        : filtered.filter(t => t.organization_id === orgFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      filtered = filtered.filter(t =>
        (t.title || '').toLowerCase().includes(q) ||
        (t.notes || '').toLowerCase().includes(q)
      );
    }
    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [monthlyIncomes, monthlyExpenses, typeFilter, categoryFilter, search, accountFilter, orgFilter]);

  // KPI hesaplamaları
  const monthlyIncomeTotal = monthlyIncomes.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const monthlyExpenseTotal = monthlyExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const monthlyNet = monthlyIncomeTotal - monthlyExpenseTotal;

  // Kasa hesabı bazlı dağılım (bu ay)
  const accountBreakdown = useMemo(() => {
    const breakdown = {};
    PAYMENT_METHODS.forEach(pm => {
      breakdown[pm.id] = { income: 0, expense: 0, net: 0, ...pm };
    });
    monthlyIncomes.forEach(i => {
      const pm = i.payment_method || 'nakit';
      if (breakdown[pm]) breakdown[pm].income += parseFloat(i.amount) || 0;
    });
    monthlyExpenses.forEach(e => {
      const pm = e.payment_method || 'nakit';
      if (breakdown[pm]) breakdown[pm].expense += parseFloat(e.amount) || 0;
    });
    Object.values(breakdown).forEach(b => { b.net = b.income - b.expense; });
    return Object.values(breakdown);
  }, [monthlyIncomes, monthlyExpenses]);

  // Bekleyen tahsilat: Hakemden alınacak + müşteri borçları
  const customerDebtTotal = useMemo(
    () => customers.filter(c => (c.balance || 0) > 0).reduce((s, c) => s + (c.balance || 0), 0),
    [customers]
  );
  const refereePending = refereesTotalBalance?.total_balance || 0;
  const totalPending = customerDebtTotal + refereePending;

  // Kategori dağılımı
  const incomeByCategory = useMemo(() => {
    const map = {};
    monthlyIncomes.forEach(i => {
      const k = i.category || 'diger';
      if (!map[k]) map[k] = { total: 0, count: 0 };
      map[k].total += parseFloat(i.amount) || 0;
      map[k].count += 1;
    });
    return Object.entries(map)
      .map(([id, v]) => ({ id, ...v, ...getCategoryMeta(id, 'income') }))
      .sort((a, b) => b.total - a.total);
  }, [monthlyIncomes]);

  const expenseByCategory = useMemo(() => {
    const map = {};
    monthlyExpenses.forEach(e => {
      const k = e.category || 'diger';
      if (!map[k]) map[k] = { total: 0, count: 0 };
      map[k].total += parseFloat(e.amount) || 0;
      map[k].count += 1;
    });
    return Object.entries(map)
      .map(([id, v]) => ({ id, ...v, ...getCategoryMeta(id, 'expense') }))
      .sort((a, b) => b.total - a.total);
  }, [monthlyExpenses]);

  // Borçlu müşteriler
  const debtorCustomers = useMemo(
    () => customers
      .filter(c => (c.balance || 0) > 0)
      .sort((a, b) => (b.balance || 0) - (a.balance || 0)),
    [customers]
  );

  // Ödenmemiş rezervasyonlar (bu ay)
  const unpaidReservations = useMemo(
    () => reservations
      .filter(r => r.payment_status === 'odenmedi')
      .sort((a, b) => new Date(b.date) - new Date(a.date)),
    [reservations]
  );

  // ===== Ay Navigasyonu =====
  const changeMonth = (dir) => {
    let nm = selectedMonth + dir;
    let ny = selectedYear;
    if (nm < 0) { nm = 11; ny -= 1; }
    else if (nm > 11) { nm = 0; ny += 1; }
    setSelectedMonth(nm);
    setSelectedYear(ny);
  };
  const goToCurrent = () => {
    setSelectedMonth(new Date().getMonth());
    setSelectedYear(new Date().getFullYear());
  };
  const isCurrentMonth = selectedMonth === new Date().getMonth() && selectedYear === new Date().getFullYear();

  // ===== CRUD =====
  const submitIncome = async (e) => {
    e.preventDefault();
    if (!incomeForm.title.trim() || !incomeForm.amount) { toast.error('Başlık ve tutar zorunludur'); return; }
    try {
      await axios.post(`${API}/incomes`, {
        title: incomeForm.title.trim(),
        amount: parseFloat(incomeForm.amount),
        category: incomeForm.category,
        date: incomeForm.date,
        payment_method: incomeForm.payment_method,
        notes: incomeForm.notes
      });
      toast.success('Gelir eklendi');
      setShowIncomeModal(false);
      setIncomeForm({ title: '', amount: '', category: 'diger', date: todayISO, payment_method: 'nakit', notes: '' });
      fetchIncomes();
      fetchAnalytics?.();
    } catch { toast.error('Gelir eklenemedi'); }
  };

  const submitExpense = async (e) => {
    e.preventDefault();
    if (!expenseForm.title.trim() || !expenseForm.amount) { toast.error('Başlık ve tutar zorunludur'); return; }
    try {
      await axios.post(`${API}/expenses`, {
        title: expenseForm.title.trim(),
        amount: parseFloat(expenseForm.amount),
        category: expenseForm.category,
        date: expenseForm.date,
        payment_method: expenseForm.payment_method,
        notes: expenseForm.notes
      });
      toast.success('Gider eklendi');
      setShowExpenseModal(false);
      setExpenseForm({ title: '', amount: '', category: 'diger', date: todayISO, payment_method: 'nakit', notes: '' });
      fetchExpenses();
      fetchAnalytics?.();
    } catch { toast.error('Gider eklenemedi'); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editingTx?.title?.trim() || !editingTx?.amount) { toast.error('Başlık ve tutar zorunludur'); return; }
    const endpoint = editingTx._type === 'income' ? 'incomes' : 'expenses';
    try {
      await axios.put(`${API}/${endpoint}/${editingTx.id}`, {
        title: editingTx.title.trim(),
        amount: parseFloat(editingTx.amount),
        category: editingTx.category,
        date: editingTx.date,
        payment_method: editingTx.payment_method || 'nakit',
        notes: editingTx.notes || ''
      });
      toast.success(editingTx._type === 'income' ? 'Gelir güncellendi' : 'Gider güncellendi');
      setShowEditModal(false);
      setEditingTx(null);
      editingTx._type === 'income' ? fetchIncomes() : fetchExpenses();
      fetchAnalytics?.();
    } catch { toast.error('Güncellenemedi'); }
  };

  const handleDelete = async (tx) => {
    if (!window.confirm(`Bu ${tx._type === 'income' ? 'geliri' : 'gideri'} silmek istiyor musunuz?\n\n"${tx.title}" — ${formatTL(tx.amount)}`)) return;
    const endpoint = tx._type === 'income' ? 'incomes' : 'expenses';
    try {
      await axios.delete(`${API}/${endpoint}/${tx.id}`);
      toast.success('Silindi');
      tx._type === 'income' ? fetchIncomes() : fetchExpenses();
      fetchAnalytics?.();
      setShowDetailModal(false);
    } catch { toast.error('Silinemedi'); }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/export/analytics`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `kasa_${MONTHS[selectedMonth]}_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel dosyası indirildi');
    } catch { toast.error('Excel indirilemedi'); }
  };

  // Mevcut filtre kategorileri (gerçekten datada var olanlar)
  const availableCategories = useMemo(() => {
    const set = new Set();
    monthlyIncomes.forEach(i => set.add(i.category));
    monthlyExpenses.forEach(e => set.add(e.category));
    const all = [...mergedIncomeCategories, ...mergedExpenseCategories];
    return [...set].map(id => all.find(c => c.id === id) || { id, label: id }).filter(Boolean);
  }, [monthlyIncomes, monthlyExpenses]);

  const clearFilters = () => {
    setSearch(''); setTypeFilter('all'); setCategoryFilter('all');
    setAccountFilter('all'); setOrgFilter('all'); setDateFrom(''); setDateTo('');
  };
  const hasActiveFilters = search || typeFilter !== 'all' || categoryFilter !== 'all'
    || accountFilter !== 'all' || orgFilter !== 'all' || customRange;

  // Organizasyon filtresi seçenekleri
  useEffect(() => {
    axios.get(`${API}/organizations`, { params: { limit: 500 } })
      .then(r => setOrgOptions((r.data?.items || r.data || []).map(o => ({ id: o.id, name: o.name }))))
      .catch(() => setOrgOptions([]));
  }, []);

  // Dışa aktarım — sunucu tarafı, ekrandaki filtrelerle birebir aynı
  const exportLedger = async (format) => {
    setExporting(format);
    const monthStart = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-01`;
    const monthEnd = new Date(selectedYear, selectedMonth + 1, 0).toISOString().slice(0, 10);
    await downloadFile(`${API}/financials/ledger/export`, {
      format,
      start: customRange ? (dateFrom || undefined) : monthStart,
      end: customRange ? (dateTo || undefined) : monthEnd,
      type: typeFilter,
      category: categoryFilter,
      payment_method: accountFilter,
      organization_id: orgFilter === 'none' ? undefined : orgFilter,
      search: search || undefined,
    }, `kasa_islem_defteri_${(customRange ? 'ozel' : `${selectedYear}_${selectedMonth + 1}`)}.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
    setExporting(null);
  };

  return (
    <div className="space-y-4" data-testid="financials-tab-root">
      {/* ============ ÜST BAR: Kurumsal sayfa başlığı ============ */}
      <PageHeader
        icon={Wallet}
        title="Kasa ve Banka"
        description={`Gelir-gider yönetimi, cari takip ve bekleyen tahsilatlar · ${periodLabel}`}
        testid="financials-page-header"
        actions={
          <>
            <div className="flex items-center rounded-lg border border-[#E2E8F0] bg-white" data-testid="month-selector">
              <Button variant="ghost" size="sm" onClick={() => changeMonth(-1)} className="h-9 px-2 text-slate-600" data-testid="prev-month-btn">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="px-2 text-[13px] font-semibold text-slate-800 min-w-[104px] text-center">
                {MONTHS[selectedMonth]} {selectedYear}
              </div>
              <Button variant="ghost" size="sm" onClick={() => changeMonth(1)} className="h-9 px-2 text-slate-600" data-testid="next-month-btn">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            {!isCurrentMonth && (
              <Button variant="outline" size="sm" onClick={goToCurrent} className="h-9 text-[12px]" data-testid="back-to-current-btn">
                <CalendarIcon className="w-3.5 h-3.5 mr-1" /> Bu Aya Dön
              </Button>
            )}
            <Button onClick={() => setShowCategoryModal(true)} variant="outline" className="h-9" data-testid="manage-categories-btn">
              <Tag className="w-4 h-4 mr-1" /> Kategoriler
            </Button>
            <Button onClick={() => setShowTransferModal(true)} variant="outline" className="h-9" data-testid="open-transfer-btn">
              <ArrowLeftRight className="w-4 h-4 mr-1" /> Transfer
            </Button>
            <Button onClick={() => setShowIncomeModal(true)} className="h-9 bg-[#15803D] hover:bg-[#126c34]" data-testid="add-income-btn">
              <Plus className="w-4 h-4 mr-1" /> Gelir
            </Button>
            <Button onClick={() => setShowExpenseModal(true)} className="h-9 bg-[#DC2626] hover:bg-[#b91c1c]" data-testid="add-expense-btn">
              <Plus className="w-4 h-4 mr-1" /> Gider
            </Button>
          </>
        }
      />

      {/* ============ KPI KARTLARI ============ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Wallet} tone={monthlyNet >= 0 ? 'sky' : 'amber'} testid="kpi-net-balance"
          label="Dönem Net" value={formatTL(monthlyNet)} sub="Gelir − Gider" />
        <StatCard icon={ArrowUpRight} tone="emerald" testid="kpi-monthly-income"
          label="Dönem Geliri" value={formatTL(monthlyIncomeTotal)} sub={`${monthlyIncomes.length} işlem`} />
        <StatCard icon={ArrowDownRight} tone="rose" testid="kpi-monthly-expense"
          label="Dönem Gideri" value={formatTL(monthlyExpenseTotal)} sub={`${monthlyExpenses.length} işlem`} />
        <StatCard icon={Hourglass} tone="amber" testid="kpi-pending-receivable"
          label="Bekleyen Tahsilat" value={formatTL(totalPending)}
          sub={`${debtorCustomers.length} müşteri${refereePending > 0 ? ' + hakemler' : ''}`} />
      </div>

      {/* ============ KASA / BANKA HESAPLARI ============ */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="account-breakdown">
        {accountBreakdown.map(acc => (
          <div key={acc.id} className={`bg-white rounded-xl border shadow-sm p-3.5 ${acc.infoOnly ? 'border-amber-200' : 'border-[#E2E8F0]'}`} data-testid={`account-card-${acc.id}`}>
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <div className={`rounded-lg p-2 ${acc.id === 'nakit' ? 'bg-emerald-50 text-emerald-700' : acc.infoOnly ? 'bg-amber-50 text-amber-700' : 'bg-sky-50 text-[#176B91]'}`}>
                  {acc.id === 'nakit' ? <Banknote className="w-4 h-4" /> : <Landmark className="w-4 h-4" />}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 text-[13px] truncate">{acc.label}</p>
                  {acc.infoOnly && <p className="text-[10px] text-amber-700 font-semibold">BİLGİ AMAÇLI · kasaya yansımaz</p>}
                </div>
              </div>
              <span className={`text-[15px] font-bold ${acc.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                {acc.net >= 0 ? '+' : ''}{formatTL(acc.net)}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div className="bg-[#F8FAFC] rounded-lg p-2 border border-[#E2E8F0]">
                <p className="text-slate-500">Giriş</p>
                <p className="font-bold text-emerald-700 text-[13px]">{formatTL(acc.income)}</p>
              </div>
              <div className="bg-[#F8FAFC] rounded-lg p-2 border border-[#E2E8F0]">
                <p className="text-slate-500">Çıkış</p>
                <p className="font-bold text-rose-700 text-[13px]">{formatTL(acc.expense)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ============ ALT SEKMELER ============ */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} data-testid="financials-subtabs">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-5 h-auto p-1 bg-white border border-[#E2E8F0] rounded-xl shadow-sm">
          <TabsTrigger value="ledger" className="data-[state=active]:bg-[#0B172A] data-[state=active]:text-white rounded-lg gap-1.5 py-2 text-[13px] text-slate-600" data-testid="tab-ledger">
            <BookOpen className="w-4 h-4" /> <span>İşlem Defteri</span>
            <Badge variant="secondary" className="ml-1 h-5 text-[10px]">{monthlyIncomes.length + monthlyExpenses.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="categories" className="data-[state=active]:bg-[#0B172A] data-[state=active]:text-white rounded-lg gap-1.5 py-2 text-[13px] text-slate-600" data-testid="tab-categories">
            <PieChart className="w-4 h-4" /> <span>Kategori Dağılımı</span>
          </TabsTrigger>
          <TabsTrigger value="accounts" className="data-[state=active]:bg-[#0B172A] data-[state=active]:text-white rounded-lg gap-1.5 py-2 text-[13px] text-slate-600" data-testid="tab-accounts">
            <UserCircle className="w-4 h-4" /> <span>Cari Hesaplar</span>
            {debtorCustomers.length > 0 && <Badge className="ml-1 h-5 text-[10px] bg-amber-500">{debtorCustomers.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="pending" className="data-[state=active]:bg-[#0B172A] data-[state=active]:text-white rounded-lg gap-1.5 py-2 text-[13px] text-slate-600" data-testid="tab-pending">
            <Clock className="w-4 h-4" /> <span>Bekleyen Tahsilat</span>
          </TabsTrigger>
          <TabsTrigger value="fixed" className="data-[state=active]:bg-[#0B172A] data-[state=active]:text-white rounded-lg gap-1.5 py-2 text-[13px] text-slate-600" data-testid="tab-fixed">
            <Clock className="w-4 h-4" /> <span>Sabit Giderler</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="fixed" className="space-y-3 mt-3">
          <FixedExpensesTab
            period={`${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`}
            onPaidChange={() => { if (fetchExpenses) fetchExpenses(); if (fetchAnalytics) fetchAnalytics(); }}
          />
        </TabsContent>

        {/* ============ TAB 1: İŞLEM DEFTERİ (gelişmiş filtre + profesyonel tablo) ============ */}
        <TabsContent value="ledger" className="space-y-3 mt-3">
          <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-3" data-testid="ledger-filters">
            <div className="flex flex-wrap items-end gap-2">
              <div className="relative flex-1 min-w-[210px]">
                <Label className="text-[11px] text-slate-500">İşlem ara</Label>
                <Search className="w-4 h-4 absolute left-3 top-[30px] text-slate-400" />
                <Input
                  placeholder="Açıklama veya not ara..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-9 bg-[#F8FAFC]"
                  data-testid="ledger-search-input"
                />
              </div>

              <div>
                <Label className="text-[11px] text-slate-500">Tür</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[128px] h-9" data-testid="ledger-type-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="income">Sadece Gelir</SelectItem>
                    <SelectItem value="expense">Sadece Gider</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[11px] text-slate-500">Kategori</Label>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-[170px] h-9" data-testid="ledger-category-filter"><SelectValue placeholder="Kategori" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Kategoriler</SelectItem>
                    {availableCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[11px] text-slate-500">Kasa / Banka</Label>
                <Select value={accountFilter} onValueChange={setAccountFilter}>
                  <SelectTrigger className="w-[150px] h-9" data-testid="ledger-account-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Hesaplar</SelectItem>
                    {PAYMENT_METHODS.map(pm => (
                      <SelectItem key={pm.id} value={pm.id}>{pm.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[11px] text-slate-500">Organizasyon</Label>
                <Select value={orgFilter} onValueChange={setOrgFilter}>
                  <SelectTrigger className="w-[170px] h-9" data-testid="ledger-org-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="none">Organizasyonsuz</SelectItem>
                    {orgOptions.map(o => (
                      <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[11px] text-slate-500">Tarih aralığı</Label>
                <div className="flex items-center gap-1">
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                    className="h-9 w-[138px]" data-testid="ledger-date-from" />
                  <span className="text-slate-400 text-xs">→</span>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                    className="h-9 w-[138px]" data-testid="ledger-date-to" />
                </div>
              </div>

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 h-9" data-testid="clear-filters-btn">
                  <X className="w-4 h-4 mr-1" /> Temizle
                </Button>
              )}

              <div className="ml-auto flex items-center gap-1.5">
                <Button variant="outline" className="h-9" disabled={exporting === 'pdf'}
                  onClick={() => exportLedger('pdf')} data-testid="ledger-export-pdf">
                  <FileDown className="w-4 h-4 mr-1" /> {exporting === 'pdf' ? 'Hazırlanıyor...' : 'PDF'}
                </Button>
                <Button className="h-9 bg-[#15803D] hover:bg-[#126c34]" disabled={exporting === 'excel'}
                  onClick={() => exportLedger('excel')} data-testid="export-excel-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-1" /> {exporting === 'excel' ? 'Hazırlanıyor...' : 'Excel'}
                </Button>
              </div>
            </div>
            {customRange && (
              <p className="text-[11px] text-[#176B91] mt-2" data-testid="ledger-range-note">
                Özel tarih aralığı uygulanıyor ({periodLabel}) — ay seçimi devre dışı.
              </p>
            )}
          </div>

          <ProTable
            testid="ledger-table"
            rows={ledger}
            rowKey={(tx) => `${tx._type}-${tx.id}`}
            onRowClick={(tx) => { setDetailTx(tx); setShowDetailModal(true); }}
            rowClassName={(tx) => (tx._type === 'income' ? 'border-l-2 border-l-emerald-400' : 'border-l-2 border-l-rose-400')}
            emptyText="Seçilen dönem ve filtrelere uygun işlem bulunamadı"
            emptyIcon={Filter}
            initialSort={{ key: 'date', dir: 'desc' }}
            columns={[
              {
                key: 'date', label: 'Tarih', width: 104, sortable: true, hideable: false,
                value: (tx) => tx.date,
                render: (tx) => <span className="text-slate-600 whitespace-nowrap">{formatDate(tx.date)}</span>,
              },
              {
                key: '_type', label: 'Tür', width: 92, sortable: true,
                render: (tx) => (tx._type === 'income'
                  ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0 text-[11px]"><ArrowUpRight className="w-3 h-3 mr-0.5" /> Gelir</Badge>
                  : <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 border-0 text-[11px]"><ArrowDownRight className="w-3 h-3 mr-0.5" /> Gider</Badge>),
              },
              {
                key: 'title', label: 'Açıklama', sortable: true, hideable: false,
                render: (tx) => (
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800 truncate max-w-[300px]">{tx.title}</div>
                    {tx.notes && <div className="text-[11px] text-slate-400 truncate max-w-[300px]">{tx.notes}</div>}
                  </div>
                ),
              },
              {
                key: 'category', label: 'Kategori', width: 150, sortable: true,
                value: (tx) => getCategoryMeta(tx.category, tx._type).label,
                render: (tx) => {
                  const meta = getCategoryMeta(tx.category, tx._type);
                  return <span className={`inline-flex items-center px-2 py-1 rounded-md text-[11px] font-medium ${meta.color}`}>{meta.label}</span>;
                },
              },
              {
                key: 'payment_method', label: 'Kasa/Banka', width: 118, sortable: true,
                value: (tx) => tx.payment_method || 'nakit',
                render: (tx) => {
                  const pmMeta = PAYMENT_METHODS.find(p => p.id === (tx.payment_method || 'nakit'));
                  return (
                    <span className="text-[12px] text-slate-600">
                      {pmMeta ? pmMeta.label : (tx.payment_method || 'Nakit')}
                      {pmMeta?.infoOnly && <span className="block text-[10px] text-amber-700 font-semibold">bilgi amaçlı</span>}
                    </span>
                  );
                },
              },
              {
                key: 'amount', label: 'Tutar', width: 136, align: 'right', sortable: true, hideable: false,
                value: (tx) => parseFloat(tx.amount) || 0,
                render: (tx) => (
                  <span className={`font-semibold whitespace-nowrap ${tx._type === 'income' ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {tx._type === 'income' ? '+' : '−'}{formatTL(tx.amount)}
                  </span>
                ),
              },
              {
                key: 'actions', label: 'İşlemler', width: 120, align: 'center', hideable: false,
                render: (tx) => (
                  <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={() => { setDetailTx(tx); setShowDetailModal(true); }} className="h-7 w-7 p-0" data-testid={`view-${tx._type}-${tx.id}`}>
                      <Eye className="w-3.5 h-3.5 text-slate-500" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingTx({ ...tx }); setShowEditModal(true); }} className="h-7 w-7 p-0" data-testid={`edit-${tx._type}-${tx.id}`}>
                      <Edit className="w-3.5 h-3.5 text-[#176B91]" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(tx)} className="h-7 w-7 p-0" data-testid={`delete-${tx._type}-${tx.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                    </Button>
                  </div>
                ),
              },
            ]}
            footer={(visible) => {
              const total = ledger.reduce((sum, t) => sum + (t._type === 'income' ? 1 : -1) * (parseFloat(t.amount) || 0), 0);
              const amountIdx = visible.findIndex(c => c.key === 'amount');
              return (
                <tr className="font-semibold text-[13px]">
                  <td colSpan={Math.max(amountIdx, 1)} className="px-3 py-2.5 text-right text-slate-600">
                    Filtre sonucu toplam ({ledger.length} işlem):
                  </td>
                  <td className={`px-3 py-2.5 text-right ${total >= 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="ledger-total">
                    {total >= 0 ? '+' : ''}{formatTL(total)}
                  </td>
                  {visible.slice(amountIdx + 1).map(c => <td key={c.key} />)}
                </tr>
              );
            }}
          />
        </TabsContent>

        {/* ============ TAB 2: KATEGORİ DAĞILIMI ============ */}
        <TabsContent value="categories" className="space-y-3 mt-3">
          <div className="grid md:grid-cols-2 gap-3">
            {/* Gelir Kategorileri */}
            <Card>
              <CardHeader className="pb-2 bg-emerald-50 border-b">
                <CardTitle className="text-base flex items-center gap-2 text-emerald-800">
                  <TrendingUp className="w-4 h-4" /> Gelir Kategorileri
                  <span className="ml-auto text-sm font-normal text-emerald-600">{formatTL(monthlyIncomeTotal)}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-2" data-testid="income-categories">
                {incomeByCategory.length === 0 ? (
                  <p className="text-center py-6 text-slate-400 text-sm">Bu ayda gelir yok</p>
                ) : incomeByCategory.map(cat => {
                  const pct = monthlyIncomeTotal > 0 ? (cat.total / monthlyIncomeTotal) * 100 : 0;
                  return (
                    <div key={cat.id} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cat.color}`}>{cat.label}</span>
                          <span className="text-xs text-slate-400">{cat.count} işlem</span>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-emerald-700">{formatTL(cat.total)}</span>
                          <span className="text-xs text-slate-500 ml-2">%{pct.toFixed(1)}</span>
                        </div>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                        <div className="bg-gradient-to-r from-emerald-400 to-emerald-600 h-full rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Gider Kategorileri */}
            <Card>
              <CardHeader className="pb-2 bg-rose-50 border-b">
                <CardTitle className="text-base flex items-center gap-2 text-rose-800">
                  <TrendingDown className="w-4 h-4" /> Gider Kategorileri
                  <span className="ml-auto text-sm font-normal text-rose-600">{formatTL(monthlyExpenseTotal)}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-2" data-testid="expense-categories">
                {expenseByCategory.length === 0 ? (
                  <p className="text-center py-6 text-slate-400 text-sm">Bu ayda gider yok</p>
                ) : expenseByCategory.map(cat => {
                  const pct = monthlyExpenseTotal > 0 ? (cat.total / monthlyExpenseTotal) * 100 : 0;
                  return (
                    <div key={cat.id} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cat.color}`}>{cat.label}</span>
                          <span className="text-xs text-slate-400">{cat.count} işlem</span>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-rose-700">{formatTL(cat.total)}</span>
                          <span className="text-xs text-slate-500 ml-2">%{pct.toFixed(1)}</span>
                        </div>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                        <div className="bg-gradient-to-r from-rose-400 to-rose-600 h-full rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============ TAB 3: CARI HESAPLAR ============ */}
        <TabsContent value="accounts" className="space-y-3 mt-3">
          <Card>
            <CardHeader className="pb-2 border-b bg-slate-50">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2 text-slate-800">
                  <Users className="w-4 h-4" /> Borçlu Müşteriler ({debtorCustomers.length})
                </CardTitle>
                <span className="text-sm font-semibold text-amber-700">Toplam: {formatTL(customerDebtTotal)}</span>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {debtorCustomers.length === 0 ? (
                <p className="text-center py-12 text-slate-400">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  Borçlu müşteri yok — tüm hesaplar kapalı 🎉
                </p>
              ) : (
                <div className="max-h-[600px] overflow-y-auto" data-testid="debtor-customers-list">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0 border-b">
                      <tr className="text-left">
                        <th className="px-4 py-3 font-medium text-slate-600">Müşteri</th>
                        <th className="px-2 py-3 font-medium text-slate-600">Telefon</th>
                        <th className="px-2 py-3 font-medium text-slate-600 text-right">Borç Tutarı</th>
                        <th className="px-2 py-3 font-medium text-slate-600 text-center w-[100px]">Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {debtorCustomers.map(c => (
                        <tr key={c.id} className="border-b hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-slate-800">{c.name}</td>
                          <td className="px-2 py-3 text-slate-600">{c.phone || '-'}</td>
                          <td className="px-2 py-3 text-right font-semibold text-amber-700">{formatTL(c.balance)}</td>
                          <td className="px-2 py-3 text-center">
                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-0 text-xs">
                              <AlertCircle className="w-3 h-3 mr-1" /> Borçlu
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ TAB 4: BEKLEYEN TAHSİLAT ============ */}
        <TabsContent value="pending" className="space-y-3 mt-3">
          <div className="grid md:grid-cols-2 gap-3">
            {/* Hakem Bakiyeleri */}
            <Card>
              <CardHeader className="pb-2 bg-purple-50 border-b">
                <CardTitle className="text-base flex items-center gap-2 text-purple-800">
                  <UserCircle className="w-4 h-4" /> Hakemden Alınacak
                  <span className="ml-auto text-sm font-normal">{formatTL(refereePending)}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 text-sm">
                {refereePending > 0 ? (
                  <div className="space-y-2">
                    <p className="text-slate-600">
                      Hakemler tarafından müşterilerden tahsil edilen, henüz kasaya teslim edilmemiş tutar.
                    </p>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-2">
                      <p className="text-xs text-purple-700 font-medium uppercase tracking-wide">Toplam Bakiye</p>
                      <p className="text-2xl font-bold text-purple-900 mt-1">{formatTL(refereePending)}</p>
                      <p className="text-xs text-purple-600 mt-1">Detay için "Hakemler" sekmesine gidin</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-center py-6 text-slate-400">Hakemden alınacak bakiye yok ✓</p>
                )}
              </CardContent>
            </Card>

            {/* Müşteri Borçları Özet */}
            <Card>
              <CardHeader className="pb-2 bg-amber-50 border-b">
                <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                  <Users className="w-4 h-4" /> Müşteri Borçları
                  <span className="ml-auto text-sm font-normal">{formatTL(customerDebtTotal)}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                {debtorCustomers.length === 0 ? (
                  <p className="text-center py-6 text-slate-400 text-sm">Borçlu müşteri yok ✓</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {debtorCustomers.slice(0, 10).map(c => (
                      <div key={c.id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded text-sm">
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 truncate">{c.name}</p>
                          <p className="text-xs text-slate-400">{c.phone || 'Telefon yok'}</p>
                        </div>
                        <span className="font-semibold text-amber-700 ml-2 whitespace-nowrap">{formatTL(c.balance)}</span>
                      </div>
                    ))}
                    {debtorCustomers.length > 10 && (
                      <p className="text-xs text-center text-slate-400 pt-2">+ {debtorCustomers.length - 10} daha fazla — Cari Hesaplar sekmesine bakın</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Ödenmemiş Rezervasyonlar */}
          <Card>
            <CardHeader className="pb-2 bg-orange-50 border-b">
              <CardTitle className="text-base flex items-center gap-2 text-orange-800">
                <AlertCircle className="w-4 h-4" /> Ödenmemiş Rezervasyonlar ({unpaidReservations.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {unpaidReservations.length === 0 ? (
                <p className="text-center py-8 text-slate-400 text-sm">Ödenmemiş rezervasyon yok ✓</p>
              ) : (
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0 border-b">
                      <tr className="text-left">
                        <th className="px-4 py-2 font-medium text-slate-600">Tarih</th>
                        <th className="px-2 py-2 font-medium text-slate-600">Saat</th>
                        <th className="px-2 py-2 font-medium text-slate-600 text-right">Toplam</th>
                        <th className="px-2 py-2 font-medium text-slate-600 text-right">Ödenen</th>
                        <th className="px-2 py-2 font-medium text-slate-600 text-right">Kalan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unpaidReservations.slice(0, 50).map(r => {
                        const remaining = (r.total_amount || 0) - (r.paid_amount || 0);
                        return (
                          <tr key={r.id} className="border-b hover:bg-slate-50">
                            <td className="px-4 py-2">{formatDate(r.date)}</td>
                            <td className="px-2 py-2 text-slate-600">{(r.start_time || '').slice(0,5)}-{(r.end_time || '').slice(0,5)}</td>
                            <td className="px-2 py-2 text-right">{formatTL(r.total_amount)}</td>
                            <td className="px-2 py-2 text-right text-emerald-600">{formatTL(r.paid_amount || 0)}</td>
                            <td className="px-2 py-2 text-right font-semibold text-orange-700">{formatTL(remaining)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============ MODALLAR ============ */}
      {/* Yeni Gelir */}
      <Dialog open={showIncomeModal} onOpenChange={setShowIncomeModal}>
        <DialogContent className="max-w-md" data-testid="income-modal">
          <DialogHeader>
            <DialogTitle className="text-emerald-700 flex items-center gap-2">
              <ArrowUpRight className="w-5 h-5" /> Yeni Gelir Ekle
            </DialogTitle>
            <DialogDescription>Kasaya gelir kaydı oluşturun</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitIncome} className="space-y-3">
            <div>
              <Label>Açıklama *</Label>
              <Input value={incomeForm.title} onChange={(e) => setIncomeForm({...incomeForm, title: e.target.value})} placeholder="Gelir açıklaması" data-testid="income-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tutar (₺) *</Label>
                <Input type="number" step="0.01" min="0" value={incomeForm.amount} onChange={(e) => setIncomeForm({...incomeForm, amount: e.target.value})} placeholder="0.00" data-testid="income-amount-input" />
              </div>
              <div>
                <Label>Tarih</Label>
                <Input type="date" value={incomeForm.date} onChange={(e) => setIncomeForm({...incomeForm, date: e.target.value})} data-testid="income-date-input" />
              </div>
            </div>
            <div>
              <Label>Kategori</Label>
              <Select value={incomeForm.category} onValueChange={(v) => setIncomeForm({...incomeForm, category: v})}>
                <SelectTrigger data-testid="income-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{mergedIncomeCategories.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Hangi Kasaya Girdi? *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                {PAYMENT_METHODS.map(pm => (
                  <button key={pm.id} type="button" onClick={() => setIncomeForm({...incomeForm, payment_method: pm.id})}
                    className={`p-2 rounded-md border-2 text-xs font-medium transition-all ${
                      incomeForm.payment_method === pm.id
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                    data-testid={`income-pm-${pm.id}`}
                  >
                    <div className="text-base mb-0.5">{pm.icon}</div>
                    <div>{pm.label}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Not</Label>
              <Textarea rows={2} value={incomeForm.notes} onChange={(e) => setIncomeForm({...incomeForm, notes: e.target.value})} placeholder="Opsiyonel açıklama" data-testid="income-notes-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowIncomeModal(false)} className="flex-1">İptal</Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="income-submit-btn">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Yeni Gider */}
      <Dialog open={showExpenseModal} onOpenChange={setShowExpenseModal}>
        <DialogContent className="max-w-md" data-testid="expense-modal">
          <DialogHeader>
            <DialogTitle className="text-rose-700 flex items-center gap-2">
              <ArrowDownRight className="w-5 h-5" /> Yeni Gider Ekle
            </DialogTitle>
            <DialogDescription>Kasaya gider kaydı oluşturun</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitExpense} className="space-y-3">
            <div>
              <Label>Açıklama *</Label>
              <Input value={expenseForm.title} onChange={(e) => setExpenseForm({...expenseForm, title: e.target.value})} placeholder="Gider açıklaması" data-testid="expense-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tutar (₺) *</Label>
                <Input type="number" step="0.01" min="0" value={expenseForm.amount} onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})} placeholder="0.00" data-testid="expense-amount-input" />
              </div>
              <div>
                <Label>Tarih</Label>
                <Input type="date" value={expenseForm.date} onChange={(e) => setExpenseForm({...expenseForm, date: e.target.value})} data-testid="expense-date-input" />
              </div>
            </div>
            <div>
              <Label>Kategori</Label>
              <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({...expenseForm, category: v})}>
                <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{mergedExpenseCategories.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Hangi Kasadan Çıktı? *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                {PAYMENT_METHODS.map(pm => (
                  <button key={pm.id} type="button" onClick={() => setExpenseForm({...expenseForm, payment_method: pm.id})}
                    className={`p-2 rounded-md border-2 text-xs font-medium transition-all ${
                      expenseForm.payment_method === pm.id
                        ? 'border-rose-500 bg-rose-50 text-rose-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                    data-testid={`expense-pm-${pm.id}`}
                  >
                    <div className="text-base mb-0.5">{pm.icon}</div>
                    <div>{pm.label}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Not</Label>
              <Textarea rows={2} value={expenseForm.notes} onChange={(e) => setExpenseForm({...expenseForm, notes: e.target.value})} placeholder="Opsiyonel açıklama" data-testid="expense-notes-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowExpenseModal(false)} className="flex-1">İptal</Button>
              <Button type="submit" className="flex-1 bg-rose-600 hover:bg-rose-700" data-testid="expense-submit-btn">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Düzenleme Modalı */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md" data-testid="edit-modal">
          <DialogHeader>
            <DialogTitle>{editingTx?._type === 'income' ? 'Geliri Düzenle' : 'Gideri Düzenle'}</DialogTitle>
          </DialogHeader>
          {editingTx && (
            <form onSubmit={submitEdit} className="space-y-3">
              <div>
                <Label>Açıklama *</Label>
                <Input value={editingTx.title || ''} onChange={(e) => setEditingTx({...editingTx, title: e.target.value})} data-testid="edit-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tutar (₺) *</Label>
                  <Input type="number" step="0.01" min="0" value={editingTx.amount || ''} onChange={(e) => setEditingTx({...editingTx, amount: e.target.value})} data-testid="edit-amount-input" />
                </div>
                <div>
                  <Label>Tarih</Label>
                  <Input type="date" value={editingTx.date || ''} onChange={(e) => setEditingTx({...editingTx, date: e.target.value})} data-testid="edit-date-input" />
                </div>
              </div>
              <div>
                <Label>Kategori</Label>
                <Select value={editingTx.category} onValueChange={(v) => setEditingTx({...editingTx, category: v})}>
                  <SelectTrigger data-testid="edit-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(editingTx._type === 'income' ? mergedIncomeCategories : mergedExpenseCategories).map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Hangi Kasa? *</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                  {PAYMENT_METHODS.map(pm => (
                    <button key={pm.id} type="button" onClick={() => setEditingTx({...editingTx, payment_method: pm.id})}
                      className={`p-2 rounded-md border-2 text-xs font-medium transition-all ${
                        (editingTx.payment_method || 'nakit') === pm.id
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      <div className="text-base mb-0.5">{pm.icon}</div>
                      <div>{pm.label}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Not</Label>
                <Textarea rows={2} value={editingTx.notes || ''} onChange={(e) => setEditingTx({...editingTx, notes: e.target.value})} data-testid="edit-notes-input" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)} className="flex-1">İptal</Button>
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="edit-submit-btn">Güncelle</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Detay Modalı */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg" data-testid="detail-modal">
          {detailTx && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detailTx._type === 'income' ? (
                    <><ArrowUpRight className="w-5 h-5 text-emerald-600" /> Gelir Detayı</>
                  ) : (
                    <><ArrowDownRight className="w-5 h-5 text-rose-600" /> Gider Detayı</>
                  )}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className={`rounded-lg p-4 ${detailTx._type === 'income' ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Tutar</p>
                  <p className={`text-3xl font-bold ${detailTx._type === 'income' ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {detailTx._type === 'income' ? '+' : '−'}{formatTL(detailTx.amount)}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Tarih</p>
                    <p className="font-medium text-slate-800 mt-0.5">{formatDate(detailTx.date)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Kategori</p>
                    <span className={`inline-block mt-0.5 px-2 py-0.5 rounded text-xs font-medium ${getCategoryMeta(detailTx.category, detailTx._type).color}`}>
                      {getCategoryMeta(detailTx.category, detailTx._type).label}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Kasa / Hesap</p>
                    {(() => {
                      const pm = detailTx.payment_method === 'enpara'
                        ? { id: 'enpara', label: 'Belirtilmemiş', icon: '', color: 'bg-slate-100 text-slate-500 border-slate-200' }
                        : (PAYMENT_METHODS.find(p => p.id === (detailTx.payment_method || 'nakit')) || PAYMENT_METHODS[0]);
                      return (
                        <span className={`inline-flex items-center gap-1 mt-0.5 px-2 py-0.5 rounded text-xs font-medium border ${pm.color}`}>
                          <span>{pm.icon}</span> {pm.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>

                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Açıklama</p>
                  <p className="font-medium text-slate-800 mt-0.5">{detailTx.title}</p>
                </div>

                {detailTx.notes && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Not</p>
                    <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{detailTx.notes}</p>
                  </div>
                )}

                {detailTx.created_at && (
                  <div className="pt-2 border-t">
                    <p className="text-xs text-slate-400">Kayıt Tarihi: {new Date(detailTx.created_at).toLocaleString('tr-TR')}</p>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => { setEditingTx({...detailTx}); setShowEditModal(true); setShowDetailModal(false); }} className="flex-1" data-testid="detail-edit-btn">
                    <Edit className="w-4 h-4 mr-1" /> Düzenle
                  </Button>
                  <Button variant="destructive" onClick={() => handleDelete(detailTx)} className="flex-1" data-testid="detail-delete-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Sil
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ KATEGORİ YÖNETİMİ MODAL ============ */}
      <CategoryManagerModal
        open={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        onChange={fetchCategoriesList}
      />

      {/* ============ KASALAR ARASI TRANSFER MODAL ============ */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="max-w-md" data-testid="transfer-modal">
          <DialogHeader>
            <DialogTitle className="text-indigo-700 flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5" /> Kasalar Arası Transfer
            </DialogTitle>
            <DialogDescription>Bir kasadan diğerine para aktarın. İki tarafa da otomatik kayıt düşer.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitTransfer} className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-rose-700">Hangi Kasadan? *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1.5">
                {PAYMENT_METHODS.map(pm => (
                  <button
                    key={pm.id}
                    type="button"
                    onClick={() => setTransferForm({...transferForm, from_method: pm.id})}
                    className={`p-2.5 rounded-md border-2 text-xs font-medium transition-all ${
                      transferForm.from_method === pm.id
                        ? 'border-rose-500 bg-rose-50 text-rose-700 shadow-sm'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                    data-testid={`transfer-from-${pm.id}`}
                  >
                    <div className="text-base mb-0.5">{pm.icon}</div>
                    <div>{pm.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-center py-1">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <ArrowDownRight className="w-5 h-5 text-indigo-600" style={{ transform: 'rotate(90deg)' }} />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-emerald-700">Hangi Kasaya? *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1.5">
                {PAYMENT_METHODS.map(pm => {
                  const disabled = pm.id === transferForm.from_method;
                  return (
                    <button
                      key={pm.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => setTransferForm({...transferForm, to_method: pm.id})}
                      className={`p-2.5 rounded-md border-2 text-xs font-medium transition-all ${
                        disabled
                          ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed'
                          : transferForm.to_method === pm.id
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                      data-testid={`transfer-to-${pm.id}`}
                    >
                      <div className="text-base mb-0.5">{pm.icon}</div>
                      <div>{pm.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tutar (₺) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={transferForm.amount}
                  onChange={(e) => setTransferForm({...transferForm, amount: e.target.value})}
                  placeholder="0.00"
                  data-testid="transfer-amount-input"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-xs">Tarih</Label>
                <Input
                  type="date"
                  value={transferForm.date}
                  onChange={(e) => setTransferForm({...transferForm, date: e.target.value})}
                  data-testid="transfer-date-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Açıklama (opsiyonel)</Label>
              <Textarea
                rows={2}
                value={transferForm.notes}
                onChange={(e) => setTransferForm({...transferForm, notes: e.target.value})}
                placeholder="Örn: Maaş ödemesi için"
                data-testid="transfer-notes-input"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowTransferModal(false)} className="flex-1" disabled={submittingTransfer}>
                İptal
              </Button>
              <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700" disabled={submittingTransfer} data-testid="transfer-submit-btn">
                {submittingTransfer ? 'İşleniyor...' : <><ArrowLeftRight className="w-4 h-4 mr-1.5" /> Transfer Yap</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancialsTab;
